import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getWhatsAppConsentStatus, listMembers } from '../api/client';
import type { TenantMember } from '../api/client';
import { useRivhitData, rivhitDisplayName } from '../context/RivhitDataContext';
import { useAuth } from '../context/AuthContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import type { RivhitCustomer } from '../context/RivhitDataContext';
import { fuzzySearchList } from '../lib/fuzzySearch';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import ConsentAuditLog from '../components/ConsentAuditLog';
import ConsentBadge from '../components/ConsentBadge';
import EditCustomerModal from '../components/EditCustomerModal';
import CustomerTableView from '../components/CustomerTableView';
import OpenBalanceWhatsAppModal from '../components/OpenBalanceWhatsAppModal';
import { printCustomerReport } from '../lib/printCustomerReport';
import { localIsraeliPhoneNumber } from '../lib/phone';

function formatSyncAge(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const BATCH_SIZE = 40;
type CustomerSortField = 'name' | 'city' | 'id';
type SortDirection = 'asc' | 'desc';

interface NewCustomerForm {
  last_name: string;
  first_name: string;
  phone: string;
  email: string;
  city: string;
  street: string;
}

const emptyForm: NewCustomerForm = {
  last_name: '',
  first_name: '',
  phone: '',
  email: '',
  city: '',
  street: '',
};

interface WhatsAppConsentStatus {
  consentStatus: string;
  consentResponseText?: string | null;
  consentResponseTimestamp?: string | null;
}

function fuzzySearch(customers: RivhitCustomer[], query: string): RivhitCustomer[] {
  return fuzzySearchList(customers, query, (c) => [
    rivhitDisplayName(c),
    [c.last_name, c.first_name].filter(Boolean).join(' '),
    c.last_name,
    c.first_name,
    c.phone,
    localIsraeliPhoneNumber(c.phone),
    c.email,
    c.city,
    c.street,
    c.id_number != null ? String(c.id_number) : undefined,
  ]);
}

export default function Customers() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const {
    configured,
    customers: allCustomers,
    customersLoading: loading,
    customersError,
    customersUpdatedAt,
    ensureCustomersLoaded,
    refreshCustomers,
    createCustomer,
    updateCustomer,
    setCustomerAllowMessage,
  } = useRivhitData();

  const [searchQuery, setSearchQuery] = useState('');
  const [whatsappFilter, setWhatsappFilter] = useState<string>('all');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<CustomerSortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [selectedCustomer, setSelectedCustomer] = useState<RivhitCustomer | null>(null);
  /** When set, open the customer modal in "enable messaging" focus mode */
  const [enableMessagingFor, setEnableMessagingFor] = useState<RivhitCustomer | null>(null);
  const [editingCustomer, setEditingCustomer] = useState<RivhitCustomer | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newForm, setNewForm] = useState<NewCustomerForm>(emptyForm);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [whatsAppCustomer, setWhatsAppCustomer] = useState<RivhitCustomer | null>(null);
  const [consentStatus, setConsentStatus] = useState<WhatsAppConsentStatus | null>(null);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const requestedInitialCustomersLoadRef = useRef(false);

  // Build reverse index: customer_id → list of assigned members
  const membersByCustomerId = useMemo(() => {
    const map = new Map<number, TenantMember[]>();
    for (const m of members) {
      for (const cid of m.assigned_customer_ids ?? []) {
        const list = map.get(cid) ?? [];
        list.push(m);
        map.set(cid, list);
      }
    }
    return map;
  }, [members]);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    if (!configured || requestedInitialCustomersLoadRef.current) return;
    requestedInitialCustomersLoadRef.current = true;
    ensureCustomersLoaded();
  }, [configured, ensureCustomersLoaded]);

  useEffect(() => {
    if (!user?.tenant_id) return;
    listMembers(user.tenant_id)
      .then((res) => setMembers(res.members.filter((m) => m.status !== 'revoked')))
      .catch(() => { /* members list is optional for badge display */ });
  }, [user?.tenant_id]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  // Keep selectedCustomer and enableMessagingFor in sync with allCustomers after edits or refreshes
  useEffect(() => {
    setSelectedCustomer((prev) => {
      if (!prev) return prev;
      const updated = allCustomers.find((c) => c.customer_id === prev.customer_id);
      return updated ?? prev;
    });
  }, [allCustomers]);

  useEffect(() => {
    setEnableMessagingFor((prev) => {
      if (!prev) return prev;
      const updated = allCustomers.find((c) => c.customer_id === prev.customer_id);
      return updated ?? prev;
    });
  }, [allCustomers]);

  const activeCustomer = selectedCustomer ?? enableMessagingFor;

  useEffect(() => {
    if (!activeCustomer) {
      setConsentStatus(null);
      return;
    }
    let active = true;
    getWhatsAppConsentStatus(String(activeCustomer.customer_id))
      .then((data) => {
        if (active) {
          setConsentStatus({
            consentStatus: data?.consentStatus ?? 'not_requested',
            consentResponseText: data?.consentResponseText ?? null,
            consentResponseTimestamp: data?.consentResponseTimestamp ?? null,
          });
        }
      })
      .catch(() => {
        if (active) setConsentStatus(null);
      });

    return () => {
      active = false;
    };
  }, [activeCustomer?.customer_id]);

  const filtered = useMemo(() => {
    let results = fuzzySearch(allCustomers, debouncedQuery);
    // WhatsApp status filter
    if (whatsappFilter === 'enabled') {
      results = results.filter((c) => c.allow_message);
    } else if (whatsappFilter === 'disabled') {
      results = results.filter((c) => !c.allow_message);
    }
    // Assignment team filter
    if (teamFilter === 'unassigned') {
      results = results.filter((c) => !membersByCustomerId.has(c.customer_id) || membersByCustomerId.get(c.customer_id)!.length === 0);
    } else if (teamFilter !== 'all') {
      results = results.filter((c) => {
        const assigned = membersByCustomerId.get(c.customer_id);
        return assigned?.some((m) => m.user_id === teamFilter) ?? false;
      });
    }
    // When a search query is active, preserve the fuzzy-match score order.
    if (!debouncedQuery.trim()) {
      results.sort((a, b) => {
        let result = 0;
        switch (sortField) {
          case 'city':
            result = (a.city || '').localeCompare(b.city || '', 'he') || rivhitDisplayName(a).localeCompare(rivhitDisplayName(b), 'he');
            break;
          case 'id':
            result = a.customer_id - b.customer_id;
            break;
          case 'name':
          default:
            result = rivhitDisplayName(a).localeCompare(rivhitDisplayName(b), 'he');
            break;
        }
        return sortDirection === 'asc' ? result : -result;
      });
    }
    return results;
  }, [allCustomers, debouncedQuery, whatsappFilter, teamFilter, membersByCustomerId, sortDirection, sortField]);

  const handleSort = (field: CustomerSortField) => {
    if (field === sortField) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(field === 'id' ? 'desc' : 'asc');
  };

  const renderSortIndicator = (field: CustomerSortField) => {
    // During fuzzy search, results are ordered by match score — no column sort applies
    if (debouncedQuery.trim()) return <span className="text-gray-300">↕</span>;
    if (field !== sortField) return <span className="text-gray-300">↕</span>;
    return <span className="text-primary-600">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  const { visible: paged, hasMore, sentinelRef } = useInfiniteScroll(filtered, BATCH_SIZE);

  const handleCreateCustomer = async () => {
    if (!newForm.last_name.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const payload: Record<string, unknown> = { last_name: newForm.last_name.trim() };
      if (newForm.first_name.trim()) payload.first_name = newForm.first_name.trim();
      if (newForm.phone.trim()) payload.phone = newForm.phone.trim();
      if (newForm.email.trim()) payload.email = newForm.email.trim();
      if (newForm.city.trim()) payload.city = newForm.city.trim();
      if (newForm.street.trim()) payload.street = newForm.street.trim();
      await createCustomer(payload as any);
      setShowAddModal(false);
      setNewForm(emptyForm);
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || err?.message || 'Failed to create customer');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('customers.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('customers.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            <button
              type="button"
              data-testid="customers-view-cards"
              aria-label={t('customers.cardView', 'Card view')}
              aria-pressed={viewMode === 'cards'}
              onClick={() => setViewMode('cards')}
              className={`px-2.5 py-1.5 text-sm transition-colors ${
                viewMode === 'cards'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title={t('customers.cardView', 'Card view')}
            >
              ⊞
            </button>
            <button
              type="button"
              data-testid="customers-view-table"
              aria-label={t('customers.tableView', 'Table view')}
              aria-pressed={viewMode === 'table'}
              onClick={() => setViewMode('table')}
              className={`px-2.5 py-1.5 text-sm border-l border-gray-300 transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title={t('customers.tableView', 'Table view')}
            >
              ☰
            </button>
          </div>
          <button
            type="button"
            onClick={() => {
              setNewForm(emptyForm);
              setCreateError(null);
              setShowAddModal(true);
            }}
            disabled={!configured}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
            title={t('customers.newCustomer', 'New Customer')}
          >
            <span aria-hidden="true">+</span>
            {t('customers.newCustomer', 'New Customer')}
          </button>
          <div className="flex flex-col items-end gap-0.5">
            <button
              onClick={refreshCustomers}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('customers.refresh', 'Refresh customers from Rivhit')}
            >
              <span className={loading ? 'animate-spin' : ''}>🔄</span>
              {t('customers.refresh', 'Refresh')}
            </button>
            {customersUpdatedAt && !loading && (
              <span className="text-xs text-gray-400">
                {t('common.lastSynced', 'Synced')} {formatSyncAge(customersUpdatedAt)}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <span className="absolute inset-y-0 ltr:left-3 rtl:right-3 flex items-center text-gray-400 pointer-events-none">
              🔍
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('customers.searchPlaceholder')}
              className="w-full rounded-md border border-gray-300 bg-gray-50 ltr:pl-10 rtl:pr-10 ltr:pr-3 rtl:pl-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
            />
          </div>
          <select
            value={whatsappFilter}
            onChange={(e) => setWhatsappFilter(e.target.value as 'all' | 'enabled' | 'disabled')}
            className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            <option value="all">{t('customerBot.whatsappFilter.all', 'All Customers')}</option>
            <option value="enabled">{t('customerBot.whatsappFilter.enabled', 'WhatsApp Enabled')}</option>
            <option value="disabled">{t('customerBot.whatsappFilter.disabled', 'WhatsApp Disabled')}</option>
          </select>
          <select
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
            className="rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
          >
            <option value="all">{t('assignments.filterAll', 'All Teams')}</option>
            <option value="unassigned">{t('assignments.filterUnassigned', 'Unassigned')}</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name || m.email}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs font-medium uppercase tracking-wider text-gray-500">
          <button type="button" onClick={() => handleSort('name')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span>{t('customers.title')}</span>
            {renderSortIndicator('name')}
          </button>
          <button type="button" onClick={() => handleSort('city')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span>{t('customers.city')}</span>
            {renderSortIndicator('city')}
          </button>
          <button type="button" onClick={() => handleSort('id')} className="inline-flex items-center gap-1 hover:text-primary-600 transition-colors">
            <span>ID</span>
            {renderSortIndicator('id')}
          </button>
        </div>
        {debouncedQuery && !loading && (
          <p className="mt-2 text-xs text-gray-500">
            {filtered.length} {t('customers.resultsFound')}
          </p>
        )}
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-400 text-sm">{t('common.loading')}</div>
      ) : customersError ? (
        <div className="p-8 text-center text-red-500 text-sm">
          <p className="mb-2">⚠️ {customersError}</p>
          <button onClick={refreshCustomers} className="text-blue-500 underline text-xs">
            {t('common.retry', 'Retry')}
          </button>
        </div>
      ) : paged.length === 0 ? (
        <div className="p-8 text-center text-gray-400 text-sm">{t('customers.noResults')}</div>
      ) : viewMode === 'table' ? (
        <>
          <CustomerTableView
            customers={paged}
            members={members}
            membersByCustomerId={membersByCustomerId}
            onUpdateCustomer={updateCustomer}
            onViewDetails={(customer) => setSelectedCustomer(customer)}
          />
          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <span className="text-sm text-gray-400">
                {paged.length} / {filtered.length}
              </span>
            </div>
          )}
          {!hasMore && filtered.length > BATCH_SIZE && (
            <p className="text-center text-xs text-gray-400 py-2">
              {filtered.length} {t('customers.resultsFound')}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {paged.map((customer) => (
              <div
                key={customer.customer_id}
                onClick={() => setSelectedCustomer(customer)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedCustomer(customer);
                  }
                }}
                className={`text-start rounded-lg border p-4 transition-all hover:shadow-md ${
                  selectedCustomer?.customer_id === customer.customer_id
                    ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold shrink-0">
                    {rivhitDisplayName(customer).charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 truncate flex items-center gap-1.5">
                      {rivhitDisplayName(customer)}
                      {customer.allow_message && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700" title={t('customerBot.whatsappFilter.enabled', 'WhatsApp Enabled')}>
                          💬
                        </span>
                      )}
                    </h3>
                    {(() => {
                      const assigned = membersByCustomerId.get(customer.customer_id);
                      if (!assigned || assigned.length === 0) return null;
                      return (
                        <p className="text-[11px] text-primary-600 mt-0.5 flex items-center gap-1" title={assigned.map((m) => m.name || m.email).join(', ')}>
                          <span>👥</span>
                          {assigned.length === 1
                            ? (assigned[0].name || assigned[0].email)
                            : t('assignments.membersCount', '{{count}} members assigned', { count: assigned.length })}
                        </p>
                      );
                    })()}
                    {customer.phone && (
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <span>📞</span> {customer.phone}
                      </p>
                    )}
                    {customer.city && (
                      <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                        <span>📍</span> {customer.city}
                      </p>
                    )}
                    {customer.allow_message ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setWhatsAppCustomer(customer);
                        }}
                        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-green-300 bg-green-50 px-2.5 py-1.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-100"
                        title={t('documents.sendWhatsApp')}
                      >
                        <span>💬</span>
                        {t('documents.sendWhatsApp')}
                      </button>
                    ) : (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        <button
                          type="button"
                          disabled
                          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-100 px-2.5 py-1.5 text-xs font-medium text-gray-400 cursor-not-allowed pointer-events-none"
                          title={t('customers.messagingDisabled', 'Messaging not enabled for this customer')}
                        >
                          <span>💬</span>
                          {t('documents.sendWhatsApp')}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEnableMessagingFor(customer);
                          }}
                          className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100"
                          title={t('customers.enableMessaging', 'Enable WhatsApp messaging')}
                        >
                          <span>✅</span>
                          {t('customers.enableMessaging', 'Enable messaging')}
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        printCustomerReport({
                          customerId: customer.customer_id,
                          customerName: rivhitDisplayName(customer),
                          t,
                          i18n,
                        });
                      }}
                      className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-400"
                      title={t('documents.printReport')}
                    >
                      <span>🖨️</span>
                      {t('documents.printReport')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCustomer(customer);
                      }}
                      className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                      title={t('customers.edit', 'Edit customer')}
                    >
                      <span>✏️</span>
                      {t('customers.edit', 'Edit')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {hasMore && (
            <div ref={sentinelRef} className="flex justify-center py-4">
              <span className="text-sm text-gray-400">
                {paged.length} / {filtered.length}
              </span>
            </div>
          )}

          {!hasMore && filtered.length > BATCH_SIZE && (
            <p className="text-center text-xs text-gray-400 py-2">
              {filtered.length} {t('customers.resultsFound')}
            </p>
          )}
        </>
      )}

      {activeCustomer && (
        <CustomerDetailsModal
          customer={activeCustomer}
          onClose={() => { setSelectedCustomer(null); setEnableMessagingFor(null); }}
          onAllowMessageChange={setCustomerAllowMessage}
          onEdit={() => setEditingCustomer(activeCustomer)}
          focusMessaging={!!enableMessagingFor}
          extraContent={(
            <div className="space-y-4">
              <ConsentBadge
                status={consentStatus?.consentStatus ?? 'not_requested'}
                showDetails
                responseText={consentStatus?.consentResponseText}
                responseTimestamp={consentStatus?.consentResponseTimestamp}
              />
              <ConsentAuditLog customerId={String(activeCustomer.customer_id)} />
            </div>
          )}
        />
      )}

      {editingCustomer && (
        <EditCustomerModal
          customer={editingCustomer}
          onClose={() => setEditingCustomer(null)}
          onSave={updateCustomer}
        />
      )}

      {whatsAppCustomer && (
        <OpenBalanceWhatsAppModal
          customerId={whatsAppCustomer.customer_id}
          displayName={rivhitDisplayName(whatsAppCustomer)}
          phone={whatsAppCustomer.phone}
          messagesEnabled={whatsAppCustomer.allow_message === true}
          onClose={() => setWhatsAppCustomer(null)}
        />
      )}

      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-gray-900">{t('customers.addCustomer', 'Add Customer')}</h2>
              <button
                onClick={() => { setShowAddModal(false); setCreateError(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors text-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('customers.lastName', 'Last Name / Company')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newForm.last_name}
                  onChange={(e) => setNewForm((form) => ({ ...form, last_name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  placeholder={t('customers.lastNamePlaceholder', 'Enter last name or company name')}
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('customers.firstName', 'First Name')}
                </label>
                <input
                  type="text"
                  value={newForm.first_name}
                  onChange={(e) => setNewForm((form) => ({ ...form, first_name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                  placeholder={t('customers.firstNamePlaceholder', 'Enter first name')}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.phone')}
                  </label>
                  <input
                    type="tel"
                    value={newForm.phone}
                    onChange={(e) => setNewForm((form) => ({ ...form, phone: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.phonePlaceholder', 'e.g. 050-1234567')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.email')}
                  </label>
                  <input
                    type="email"
                    value={newForm.email}
                    onChange={(e) => setNewForm((form) => ({ ...form, email: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.emailPlaceholder', 'email@example.com')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.city')}
                  </label>
                  <input
                    type="text"
                    value={newForm.city}
                    onChange={(e) => setNewForm((form) => ({ ...form, city: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.cityPlaceholder', 'Enter city')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('customers.street', 'Street')}
                  </label>
                  <input
                    type="text"
                    value={newForm.street}
                    onChange={(e) => setNewForm((form) => ({ ...form, street: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none"
                    placeholder={t('customers.streetPlaceholder', 'Enter street address')}
                  />
                </div>
              </div>

              {createError && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  {createError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowAddModal(false); setCreateError(null); setNewForm(emptyForm); }}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateCustomer}
                disabled={creating || !newForm.last_name.trim()}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {creating ? t('customers.creating', 'Creating...') : t('customers.create', 'Create Customer')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
